import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { apiPost } from "../api";
import { useAuth } from "../auth";
import { Icons } from "../components/icons";
import { createPortal } from "react-dom";
import { useGetOrganizationsQuery } from "../store/organizations";

export default function Organizations() {
  const { user, refreshUser } = useAuth();
  // RTK Query — el cache se comparte con Subscribers.tsx que también consulta orgs.
  const { data: orgs = [], isLoading: loading, refetch: load } = useGetOrganizationsQuery(undefined, {
    skip: !user?.is_platform_admin, // solo el platform admin tiene acceso
  });
  const [showCreate, setShowCreate] = useState(false);

  if (!user?.is_platform_admin) {
    return (
      <div className="page-shell">
        <header className="page-header">
          <div>
            <h1>Organizaciones</h1>
          </div>
        </header>
        <p style={{ padding: 24, color: "var(--fg-3)" }}>
          Solo el platform admin puede ver esta página.
        </p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <header className="page-header">
        <div>
          <h1>Organizaciones</h1>
          <div className="sub">
            Tenants registrados en la plataforma · {orgs.length} organizacion
            {orgs.length === 1 ? "" : "es"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icons.Plus size={13} sw={2.2} />
          <span style={{ marginLeft: 6 }}>Nueva organización</span>
        </button>
      </header>
      <div className="page-content" style={{ display: "grid", gap: 18 }}>
        <section className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Organización</th>
                <th style={{ width: 180 }}>Slug</th>
                <th style={{ width: 160 }}>Creada</th>
                <th style={{ width: 110, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td>
                    <Link
                      to={`/organizations/${o.id}`}
                      style={{ color: "var(--fg-1)", fontWeight: 500, textDecoration: "none" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: "color-mix(in oklab, var(--accent) 12%, transparent)",
                            color: "var(--accent)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: "var(--font-mono)",
                          }}
                        >
                          {o.name.slice(0, 2).toUpperCase()}
                        </span>
                        <span>{o.name}</span>
                      </div>
                    </Link>
                  </td>
                  <td className="mono" style={{ color: "var(--fg-2)", fontSize: 12 }}>{o.slug}</td>
                  <td className="mono" style={{ color: "var(--fg-3)", fontSize: 12 }}>
                    {new Date(o.inserted_at).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <Link
                      to={`/organizations/${o.id}`}
                      className="btn btn-icon btn-ghost"
                      title="Ver detalle"
                      style={{ width: 28, height: 28, display: "inline-flex" }}
                    >
                      <Icons.External size={13} />
                    </Link>
                  </td>
                </tr>
              ))}
              {!loading && orgs.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 0 }}>
                    <div className="empty" style={{ padding: 48 }}>
                      <div className="ico"><Icons.Cube size={20} /></div>
                      <div>Sin organizaciones todavía.</div>
                      <div className="text-sm" style={{ marginTop: 6 }}>
                        Cada organización es un tenant aislado: tiene sus propios servicios, usuarios y subscriptores.
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={4} style={{ padding: 24, color: "var(--fg-3)", textAlign: "center" }}>
                    Cargando…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
            // Refrescá el user para que el OrgSelector del topbar muestre
            // la org recién creada sin necesidad de logout+login.
            void refreshUser();
          }}
        />
      )}
    </div>
  );
}

function CreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!slug.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiPost("/v1/organizations", {
        slug: slug.trim(),
        name: name.trim(),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div onClick={onClose} className="modal-backdrop">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="modal-card"
        style={{ width: 480, display: "grid", gap: 14 }}
      >
        <header>
          <h2 style={{ margin: 0, fontSize: 18 }}>Nueva organización</h2>
          <p
            className="text-sm text-muted"
            style={{ marginTop: 4, marginBottom: 0 }}
          >
            La organización es un tenant: cada usuario y servicio pertenece a
            una.
          </p>
        </header>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>
            Nombre
          </span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Nuevo León"
            required
          />
        </label>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--fg-2)" }}>
            Slug
          </span>
          <input
            className="input"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="nuevo-leon"
            required
          />
          <span className="hint">
            Solo lowercase, números, guiones. Aparece en URLs y env vars.
          </span>
        </label>
        {error && (
          <p style={{ color: "var(--st-major)", fontSize: 13, margin: 0 }}>
            {error}
          </p>
        )}
        <footer style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !slug.trim() || !name.trim()}
          >
            {submitting ? "Creando…" : "Crear"}
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
}
